import { dayId, type DayId, type GameState } from '../core/state';
import { typhoonDay } from './weather';

/**
 * Pure rules of the classroom package (no DOM): which classroom cases happen this
 * Friday, when and where, and which school event the day gets. Kept apart so the
 * NPC schedules and the tests can use it without pulling in the UI.
 */

export type CaseId = 'late' | 'homework' | 'standing' | 'ball';
export const CASES: readonly CaseId[] = ['late', 'homework', 'standing', 'ball'];
export type KidId = 'xiaohua' | 'xiaomei' | 'azhe' | 'xiaoqiang' | 'xiaofang';
export const KIDS: readonly KidId[] = ['xiaohua', 'xiaomei', 'azhe', 'xiaoqiang', 'xiaofang'];

/** Who each case is about (the first kid is the one you talk to). */
export const CASE_KIDS: Record<CaseId, readonly KidId[]> = {
  late: ['xiaohua'],
  homework: ['xiaomei'],
  standing: ['azhe'],
  ball: ['xiaoqiang', 'xiaofang'],
};

const H = (h: number, m = 0) => h * 60 + m;
/** When each case can be found (game minutes). */
export const CASE_WINDOWS: Record<CaseId, readonly [number, number]> = {
  late: [H(8), H(8, 45)],
  homework: [H(8), H(12)],
  standing: [H(9), H(12)],
  ball: [H(12), H(13, 30)],
};

/**
 * 1–2 cases each Friday, two new ones every week so all four come round within two
 * weeks; every third week is a calmer Friday with just one.
 */
export function casesForWeek(week: number): CaseId[] {
  const base = (2 * Math.max(0, week - 1)) % CASES.length;
  const list = [CASES[base], CASES[(base + 1) % CASES.length]];
  return week % 3 === 0 ? list.slice(0, 1) : list;
}

export const caseDoneKey = (c: CaseId) => `cls.case.${c}`;

/** Is the case on today (Friday, this week's rotation, not resolved yet)? Ignores the clock. */
export function caseToday(s: GameState, c: CaseId): boolean {
  // No school (and so no cases) on a typhoon day off.
  return dayId(s) === 'fri' && !typhoonDay(s.dayIndex) && casesForWeek(s.week).includes(c) && !s.daily[caseDoneKey(c)];
}

/** On today and inside its time window: the kid is waiting at the case spot. */
export function caseLive(s: GameState, c: CaseId): boolean {
  const [a, b] = CASE_WINDOWS[c];
  return caseToday(s, c) && s.time >= a && s.time < b;
}

export function caseOfKid(k: KidId): CaseId {
  return (Object.keys(CASE_KIDS) as CaseId[]).find((c) => CASE_KIDS[c].includes(k))!;
}

// ---------------------------------------------------------------------------
// School events: a weighted scheduler, at most one a day.
// ---------------------------------------------------------------------------

export type EventId = 'forgotHomework' | 'lostPencil' | 'misunderstanding' | 'bullying' | 'contest' | 'quiz' | 'parentsMeeting';
export const EVENTS: readonly EventId[] = ['forgotHomework', 'lostPencil', 'misunderstanding', 'bullying', 'contest', 'quiz', 'parentsMeeting'];

export interface EventContext {
  day: DayId;
  week: number;
  /** Game day number (week * 3 + dayIndex). */
  dayNum: number;
  /** Day number of the last time the homework chore was done (-1 if never). */
  homeworkDay: number;
  /** Day number each event last happened. */
  lastSeen: Partial<Record<EventId, number>>;
}

/** Parents' meeting: every 4th Friday, in the evening. */
export const isMeetingWeek = (week: number) => week % 4 === 0;

/** Homework is set on Friday and due the next Friday: forgotten if not done on any day since. */
export function homeworkForgotten(ctx: EventContext): boolean {
  if (ctx.day !== 'fri' || ctx.week < 2) return false;
  return ctx.homeworkDay < ctx.dayNum - 3;
}

interface Rule {
  id: EventId;
  weight: number;
  when: (ctx: EventContext) => boolean;
  /** Days before it may happen again. */
  gap: number;
  /** Always happens when its condition holds (the parents' meeting is on the calendar). */
  force?: boolean;
}

const RULES: readonly Rule[] = [
  { id: 'parentsMeeting', weight: 0, gap: 6, force: true, when: (c) => c.day === 'fri' && isMeetingWeek(c.week) },
  { id: 'forgotHomework', weight: 60, gap: 3, when: homeworkForgotten },
  { id: 'lostPencil', weight: 3, gap: 6, when: (c) => c.day === 'fri' },
  { id: 'misunderstanding', weight: 3, gap: 6, when: (c) => c.day === 'fri' && c.week >= 2 },
  { id: 'contest', weight: 3, gap: 6, when: (c) => c.day === 'fri' && c.week >= 2 },
  { id: 'quiz', weight: 3, gap: 3, when: (c) => c.day === 'fri' },
  { id: 'bullying', weight: 3, gap: 9, when: (c) => c.day !== 'fri' && c.week >= 1 },
];
/** Weight of "nothing special today", so weekends stay mostly calm. */
const QUIET: Record<DayId, number> = { fri: 1, sat: 4, sun: 4 };

/** Pick today's event (or null). `rnd` in [0,1). */
export function pickEvent(ctx: EventContext, rnd: number): EventId | null {
  const pool = RULES.filter((r) => r.when(ctx) && !(ctx.lastSeen[r.id] !== undefined && ctx.dayNum - ctx.lastSeen[r.id]! < r.gap));
  const forced = pool.find((r) => r.force);
  if (forced) return forced.id;
  const total = pool.reduce((a, r) => a + r.weight, 0) + QUIET[ctx.day];
  let k = rnd * total;
  for (const r of pool) {
    k -= r.weight;
    if (k < 0) return r.id;
  }
  return null;
}

/**
 * Spelling bee: your three answers plus your English passport XP against the
 * other finalists. More XP → the rivals are more likely to slip first.
 */
export function contestWins(xp: number, correct: number, rnd: number): boolean {
  if (correct === 3) return rnd < 0.55 + Math.min(0.4, xp / 400);
  if (correct === 2) return rnd < 0.15 + Math.min(0.4, xp / 500);
  return false;
}

/** A hobby's level from its practice points (1 point per session). */
export function hobbyLevel(points: number): number {
  return Math.min(10, 1 + Math.floor(Math.sqrt(Math.max(0, points) * 1.5)));
}

/** Two plausible wrong spellings of `word` for the spelling bee (never the word itself). */
export function misspellings(word: string): [string, string] {
  const w = word;
  const n = w.length;
  const cands: string[] = [];
  const dbl = w.match(/(.)\1/);
  if (dbl) cands.push(w.replace(/(.)\1/, '$1'));
  if (w.includes('ie')) cands.push(w.replace('ie', 'ei'));
  else if (w.includes('ei')) cands.push(w.replace('ei', 'ie'));
  for (let i = 1; i < n - 2; i++) {
    if (w[i] !== w[i + 1]) cands.push(w.slice(0, i) + w[i + 1] + w[i] + w.slice(i + 2));
  }
  const lastVowel = w.slice(1).search(/[aeiou][^aeiou]*$/);
  if (lastVowel >= 0) cands.push(w.slice(0, lastVowel + 1) + w.slice(lastVowel + 2));
  for (let i = 2; i < n - 1; i++) if (w[i] !== w[i - 1]) cands.push(w.slice(0, i) + w[i] + w.slice(i));
  const uniq = [...new Set(cands)].filter((c) => c !== w && c.length > 1);
  // Mix the kinds of mistake: one from the front of the list, one from the middle.
  const a = uniq[0] ?? `${w}e`;
  const b = uniq[Math.floor(uniq.length / 2)] !== a ? uniq[Math.floor(uniq.length / 2)] ?? `${w}s` : uniq[uniq.length - 1] ?? `${w}s`;
  return [a, b === a ? `${w}s` : b];
}
